import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { CellExportFacade, NotebookCellFacade, NotebookFacade, NotebookTypes } from '@mode/notebooks/contract';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { NotebookState } from '@mode/notebooks/contract';
import { NotebookService, NotebookStateService } from '@mode/notebooks/domain';
import { TooltipPlacement } from '@mode/capra';
import { getThoughtspotIntegrated } from '@mode/shared/util-js';

@Component({
  selector: 'mode-notebook-toolbar',
  templateUrl: './notebook-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotebookToolbarComponent implements OnInit, OnDestroy {
  @Input() notebookToken!: string;
  @Input() session: any;

  public enableNotebookExportDatasets$!: Observable<boolean>;
  public cellType!: 'code' | 'markdown'; // for the select dropdown
  private contentType$!: Observable<NotebookTypes.ContentType>;

  private destroy$ = new Subject<void>();

  public report: any;
  readonly tooltipPlacement: TooltipPlacement = 'bottom';

  private notebookState$!: Observable<NotebookState>;

  public selectedNotebookToken$!: Observable<string>;
  public cellToken$!: Observable<string>;
  public nextCellToken$!: Observable<string>;
  public prevCellToken$!: Observable<string>;
  public cellExecuting$!: Observable<boolean>;
  public isInLayout$!: Observable<boolean>;
  private isEditing$!: Observable<boolean>;
  private isFrozen$!: Observable<boolean>;
  private isFolded$!: Observable<boolean>;
  private isPython$!: Observable<boolean>;
  private isMarkdown$!: Observable<boolean>;

  public canRestartSession$!: Observable<boolean>;
  private canDelete$!: Observable<boolean>;
  private canFreeze$!: Observable<boolean>;
  public canRunCell$!: Observable<boolean>;

  public hasOutput$!: Observable<boolean>;
  public canHideOutput$!: Observable<boolean>;
  private hasResult$!: Observable<boolean>;

  public canRun = false;
  public isCancelling = false;
  public isRunning = false;
  public runAllIcon = '';

  private hasNotebookContentLink$!: Observable<boolean>;
  public notebookContentLink$!: Observable<string>;

  public isSelectedCellExporting$!: Observable<boolean>;

  public thoughtspotIntegrated: boolean | null = false;

  constructor(
    private notebookCellFacade: NotebookCellFacade,
    private notebookStateService: NotebookStateService,
    private featureFlagService: FeatureFlagsFacade,
    private cellExportFacade: CellExportFacade,
    private notebookService: NotebookService,
    private notebookFacade: NotebookFacade
  ) {
    this.thoughtspotIntegrated = getThoughtspotIntegrated(window.document);
  }

  ngOnInit(): void {
    this.selectedNotebookToken$ = this.notebookStateService.selectedNotebookToken$;
    this.notebookState$ = this.notebookStateService.notebookState$;
    this.cellToken$ = this.notebookStateService.cellToken$;
    this.cellExecuting$ = this.notebookStateService.cellExecuting$;
    this.prevCellToken$ = this.notebookStateService.prevCellToken$;
    this.nextCellToken$ = this.notebookStateService.nextCellToken$;
    this.isInLayout$ = this.notebookStateService.isInLayout$;
    this.hasResult$ = this.notebookStateService.hasResult$;
    this.hasOutput$ = this.notebookStateService.hasOutput$;
    this.canHideOutput$ = this.notebookStateService.canHideOutput$;
    this.contentType$ = this.notebookStateService.contentType$;
    this.isEditing$ = this.notebookStateService.isEditing$;
    this.isFolded$ = this.notebookStateService.isFolded$;
    this.isFrozen$ = this.notebookStateService.isFrozen$;
    this.isMarkdown$ = this.contentType$.pipe(map((c) => c === NotebookTypes.ContentType.Markdown));
    this.isPython$ = this.contentType$.pipe(map((c) => c === NotebookTypes.ContentType.Python));
    this.canDelete$ = this.notebookStateService.canDelete$;
    this.canFreeze$ = this.notebookStateService.canFreeze$;
    this.canRunCell$ = this.notebookStateService.canRunCell$(this.notebookToken);
    this.canRestartSession$ = this.notebookStateService.canRestartSession$;

    // Watch notebook state
    this.notebookState$.pipe(takeUntil(this.destroy$)).subscribe((state: any) => {
      this.isCancelling = state === NotebookState.Cancelling;
      this.isRunning = state === NotebookState.Running;
      this.canRun = state === NotebookState.Ready || state === NotebookState.Cancelled;
      this.runAllIcon = this.isCancelling ? '' : this.isRunning ? 'icon-stop' : 'icon-play';
    });
    this.contentType$
      .pipe(
        takeUntil(this.destroy$),
        map((t) => (t === NotebookTypes.ContentType.Markdown ? 'markdown' : 'code'))
      )
      .subscribe((type) => (this.cellType = type));

    // Feature flag
    this.enableNotebookExportDatasets$ = this.featureFlagService
      .asObservable(FeatureFlag.NotebookExportDatasets)
      .pipe(takeUntil(this.destroy$));

    // Export status by cell
    this.isSelectedCellExporting$ = this.cellToken$.pipe(
      switchMap((cellToken) => this.cellExportFacade.isExportingByCell$(cellToken))
    );

    // Export link for notebook
    this.hasNotebookContentLink$ = this.notebookStateService.notebookResource$.pipe(map((r) => r.hasLink('export')));

    this.notebookContentLink$ = this.notebookStateService.notebookResource$.pipe(
      map((r: any) => r.links('export').$expand({ markdown_comments: true, cell_headers: false }))
    );
  }

  delete(cellToken: string) {
    if (cellToken) {
      this.notebookCellFacade.removeCell(this.notebookToken, cellToken);
    }
  }

  runCell(params: { cellToken: string; contentType: NotebookTypes.ContentType; nextCellToken: string }) {
    const { cellToken, contentType, nextCellToken } = params;
    // currently rely on blur event to save markdown
    if (contentType !== NotebookTypes.ContentType.Markdown) {
      this.notebookCellFacade.runCell(this.notebookToken, cellToken);
    }

    // TODO: Consider a Run cell and select next action
    if (!nextCellToken) {
      this.notebookCellFacade.addCell({ notebookToken: this.notebookToken });
    } else {
      this.notebookCellFacade.selectCell(nextCellToken, false);
    }
  }

  restartSession() {
    this.notebookService.initSession(this.notebookToken, false);
  }

  toggleFreezeCell(cellToken: string) {
    this.notebookCellFacade.toggleFreezeCell(cellToken);
  }

  toggleFoldCell(cellToken: string) {
    this.notebookCellFacade.foldCell(cellToken);
  }

  addToLayout(cellToken: string) {
    this.notebookCellFacade.addCellToLayout(cellToken);
  }

  removeFromLayout(cellToken: string) {
    this.notebookCellFacade.removeCellFromLayout(cellToken);
  }

  insertCellAbove(cellToken: string) {
    this.notebookCellFacade.addCell({ notebookToken: this.notebookToken, nextToken: cellToken });
  }

  insertCellBelow(nextCellToken: string) {
    this.notebookCellFacade.addCell({ notebookToken: this.notebookToken, nextToken: nextCellToken });
  }

  moveCellUp(cellToken: string) {
    this.notebookCellFacade.moveCell(cellToken, false);
  }

  moveCellDown(cellToken: string) {
    this.notebookCellFacade.moveCell(cellToken, true);
  }

  // TODO: update to support R
  updateContentType(cellToken: string) {
    this.notebookCellFacade.updateContentType(
      cellToken,
      this.cellType === 'code' ? NotebookTypes.ContentType.Python : NotebookTypes.ContentType.Markdown
    );
  }

  addRemoveFromLayout(params: { cellToken: string; inLayout: boolean }) {
    const { cellToken, inLayout } = params;
    if (!cellToken) {
      return;
    }

    if (inLayout) {
      this.removeFromLayout(cellToken);
    } else {
      this.addToLayout(cellToken);
    }
  }

  runOrCancelNotebook() {
    if (this.isRunning) {
      this.notebookService.cancelRun(this.notebookToken);
    } else {
      if (this.canRun) {
        this.notebookFacade.runNotebook(this.notebookToken);
      }
    }
  }

  clearCellOutputs() {
    this.notebookService.clearAllCellOutputs(this.notebookToken);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
